import React, { useEffect, useState } from 'react';
import { func, string } from 'prop-types';
import classNames from 'classnames';
import { Form as FinalForm } from 'react-final-form';
import { FormattedMessage, injectIntl } from '../../util/reactIntl';

import {
  Form,
  Modal,
  Button,
  CustomExtendedDataField,
  FieldLocationAutocompleteInput,
} from '../../components';
import arrayMutators from 'final-form-arrays';
import css from './SellerProfileDetailsModal.module.css';
import { getUserType } from '../../util/userDataExtraction';
import { getPropsForCustomUserFieldInputs } from '../../util/userHelpers';
import { isProfileDetailsExists, removePubPrefix } from '../../util/genericHelpers';
import { useDispatch } from 'react-redux';
import { updateProfileAndListing } from '../../containers/ProfileSettingsPage/ProfileSettingsPage.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { pathByRouteName } from '../../util/routes';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import {
  autocompletePlaceSelected,
  autocompleteSearchRequired,
  composeValidators,
} from '../../util/validators';
const identity = v => v;

const SellerProfileDetailsForm = props => (
  <FinalForm
    {...props}
    mutators={{ ...arrayMutators }}
    keepDirtyOnReinitialize={true}
    render={fieldRenderProps => {
      const {
        className,
        rootClassName,
        disabled,
        handleSubmit,
        intl,
        formId,
        invalid,
        form,
        currentUser,
        userFields,
        values,
      } = fieldRenderProps;

      const addressRequiredMessage = intl.formatMessage({
        id: 'EditListingLocationForm.addressRequired',
      });
      const addressNotRecognizedMessage = intl.formatMessage({
        id: 'EditListingLocationForm.addressNotRecognized',
      });

      const classes = classNames(rootClassName || css.formRoot, className);
      const submitDisabled = invalid || disabled;
      // const required = validators.required('This field is required');

      const userType = getUserType(currentUser);
      const userFieldProps = getPropsForCustomUserFieldInputs(userFields, intl, userType);
      return (
        <Form
          className={classes}
          onSubmit={e => {
            handleSubmit(e);
          }}
        >
          {userType === 'seller' ? (
            <div className={css.customFields}>
              {userFieldProps.map(({ key, ...fieldProps }) => (
                <CustomExtendedDataField key={key} {...fieldProps} formId={formId} />
              ))}
            </div>
          ) : null}

          <FieldLocationAutocompleteInput
            rootClassName={css.locationAddress}
            inputClassName={css.locationAutocompleteInput}
            iconClassName={css.locationAutocompleteInputIcon}
            predictionsClassName={css.predictionsRoot}
            validClassName={css.validLocation}
            name="location"
            label={intl.formatMessage({ id: 'EditListingLocationForm.address' })}
            placeholder={intl.formatMessage({
              id: 'EditListingLocationForm.addressPlaceholder',
            })}
            useDefaultPredictions={false}
            format={identity}
            valueFromForm={values.location}
            validate={composeValidators(
              autocompleteSearchRequired(addressRequiredMessage),
              autocompletePlaceSelected(addressNotRecognizedMessage)
            )}
          />
          <Button
            className={css.submitButton}
            type="submit"
            inProgress={false}
            disabled={submitDisabled}
          >
            {intl.formatMessage({ id: 'SellerProfileDetails.submit' })}
          </Button>
        </Form>
      );
    }}
  />
);

// Show refund form
const SellerProfileDetailsInfo = props => {
  const { authorListingId, onCloseModal } = props || {};
  const dispatch = useDispatch();
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const handleSubmitDetails = async v => {
    const values = removePubPrefix(v);
    const { location, ...restValues } = values;
    const {
      selectedPlace: { address, origin },
    } = location;

    const listingData = {
      geolocation: origin,
      publicData: {
        ...values,
        location: { address },
      },
    };
    const geolocation = JSON.stringify(origin);
   
    const profileData = {
      publicData: { location: { address }, geolocation, ...restValues },
    };

    await dispatch(updateProfileAndListing({ authorListingId, profileData, listingData }));
    dispatch(fetchCurrentUser());
    const profilePath = pathByRouteName('ProfileSettingsPage', routeConfiguration, {});
    history.push(profilePath);
    onCloseModal();
  };

  return (
    <>
      <h4 className={css.modalTitle}>
        <FormattedMessage id="SellerProfileDetails.title" />
      </h4>
      <SellerProfileDetailsForm
        onSubmit={v => {
          handleSubmitDetails(v);
        }}
        {...props}
      />
    </>
  );
};

// SellerProfileDetails modal
const SellerProfileDetailsModal = props => {
  const {
    className,
    rootClassName,
    id,
    intl,
    isSellerDetails,
    onCloseModal,
    onManageDisableScrolling,
    listing,
    currentUser,
    userFields,
    setIsSellerDetails,
  } = props;

  const classes = classNames(rootClassName || css.root, className);
  const authorListingId = currentUser?.attributes?.profile?.privateData?.authorListingId;
  const hasProfileDetails = isProfileDetailsExists(currentUser);

  useEffect(() => {
    if (!hasProfileDetails) {
      setIsSellerDetails(true);
    }
  }, [currentUser, hasProfileDetails]);

  return (
    <Modal
      id={id}
      containerClassName={classes}
      contentClassName={css.modalContent}
      isOpen={isSellerDetails}
      onClose={onCloseModal}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
      closeButtonMessage={intl.formatMessage({ id: 'SellerProfileDetails.close' })}
    >
      <SellerProfileDetailsInfo
        intl={intl}
        listing={listing}
        currentUser={currentUser}
        userFields={userFields}
        authorListingId={authorListingId}
        onCloseModal={onCloseModal}
      />
    </Modal>
  );
};

SellerProfileDetailsModal.defaultProps = {
  className: null,
  rootClassName: null,
};

SellerProfileDetailsModal.propTypes = {
  className: string,
  rootClassName: string,
  id: string.isRequired,
  onCloseModal: func.isRequired,
  onManageDisableScrolling: func.isRequired,
};

export default injectIntl(SellerProfileDetailsModal);
